import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gear, Info, Plus, Trash, Tag, Globe } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = [
  "#4CAF50", "#F44336", "#FF9800", "#FFC107", "#9C27B0",
  "#2196F3", "#00BCD4", "#E91E63", "#795548", "#607D8B",
];

const Settings = () => {
  const [statuses, setStatuses] = useState([]);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#4CAF50");
  const [dolphinToken, setDolphinToken] = useState("");
  const [dolphinSaving, setDolphinSaving] = useState(false);
  const [dolphinLoaded, setDolphinLoaded] = useState(false);
  const [botUsername, setBotUsername] = useState("");
  const [botSaving, setBotSaving] = useState(false);

  useEffect(() => {
    fetchStatuses();
    fetchDolphinToken();
    fetchBotUsername();
  }, []);

  const fetchBotUsername = async () => {
    try {
      const res = await axios.get(`${API}/settings/bot-username`);
      setBotUsername(res.data.username || "");
    } catch { /* */ }
  };

  const saveBotUsername = async () => {
    setBotSaving(true);
    try {
      await axios.post(`${API}/settings/bot-username`, { username: botUsername });
      toast.success("Bot username saved");
    } catch { toast.error("Error saving"); }
    finally { setBotSaving(false); }
  };

  const fetchDolphinToken = async () => {
    try {
      const res = await axios.get(`${API}/settings/dolphin`);
      setDolphinToken(res.data.token || "");
      setDolphinLoaded(true);
    } catch { setDolphinLoaded(true); }
  };

  const saveDolphinToken = async () => {
    setDolphinSaving(true);
    try {
      await axios.post(`${API}/settings/dolphin`, { token: dolphinToken });
      toast.success("Dolphin Anty API token saved");
    } catch { toast.error("Error saving"); }
    finally { setDolphinSaving(false); }
  };

  const fetchStatuses = async () => {
    try {
      const res = await axios.get(`${API}/custom-statuses`);
      setStatuses(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error("Введите название статуса");
      return;
    }
    try {
      await axios.post(`${API}/custom-statuses`, {
        name: newName.trim(),
        color: newColor,
      });
      toast.success("Статус создан");
      setNewName("");
      fetchStatuses();
    } catch (e) {
      toast.error("Ошибка создания");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/custom-statuses/${id}`);
      toast.success("Статус удалён");
      fetchStatuses();
    } catch (e) {
      toast.error("Ошибка удаления");
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="settings-page">
      {/* Top bar */}
      <div className="top-bar">
        <div className="top-bar-left">
          <Gear size={24} weight="duotone" className="text-[#8A9099]" />
          <h1 className="text-lg font-semibold">Настройки</h1>
        </div>
      </div>

      {/* Content */}
      <div className="content-area">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Custom statuses */}
          <div className="bg-[#1A1D24] rounded-lg border border-[#252A33] p-6" data-testid="custom-statuses-section">
            <h2 className="text-sm font-semibold text-[#8A9099] mb-4 flex items-center gap-2">
              <Tag size={16} weight="duotone" />
              ПОЛЬЗОВАТЕЛЬСКИЕ СТАТУСЫ
            </h2>

            {/* Create new status */}
            <div className="flex items-end gap-3 mb-4">
              <div className="flex-1">
                <Label className="text-xs text-[#8A9099]">Название</Label>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Например: Активный, На паузе..."
                  className="mt-1 bg-[#0D0F12] border-[#252A33]"
                  data-testid="status-name-input"
                  onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                />
              </div>
              <div>
                <Label className="text-xs text-[#8A9099]">Цвет</Label>
                <div className="flex gap-1.5 mt-1">
                  {STATUS_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setNewColor(c)}
                      className={`w-7 h-7 rounded ${newColor === c ? "ring-2 ring-white ring-offset-1 ring-offset-[#1A1D24]" : ""}`}
                      style={{ backgroundColor: c }}
                      data-testid={`status-color-${c}`}
                    />
                  ))}
                </div>
              </div>
              <button
                onClick={handleCreate}
                className="action-btn action-btn-primary h-9"
                data-testid="create-status-btn"
              >
                <Plus size={14} weight="bold" />
                Добавить
              </button>
            </div>

            {/* Statuses list */}
            {statuses.length === 0 ? (
              <p className="text-sm text-[#5C6370] text-center py-4">
                Нет пользовательских статусов. Создайте первый выше.
              </p>
            ) : (
              <div className="space-y-2">
                {statuses.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between bg-[#0D0F12] rounded p-3 border border-[#252A33]"
                    data-testid={`status-item-${s.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="w-4 h-4 rounded-full"
                        style={{ background: s.color }}
                      />
                      <span className="text-sm font-medium">{s.name}</span>
                    </div>
                    <button
                      onClick={() => handleDelete(s.id)}
                      className="icon-btn text-[#5C6370] hover:text-red-400"
                      data-testid={`delete-status-${s.id}`}
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Telegram Bot Username */}
          <div className="bg-[#1A1D24] rounded-lg border border-[#252A33] p-6" data-testid="bot-settings">
            <h2 className="text-sm font-semibold text-[#8A9099] mb-4 flex items-center gap-2">
              <Globe size={16} weight="duotone" className="text-[#2196F3]" />
              TELEGRAM BOT
            </h2>
            <p className="text-xs text-[#5C6370] mb-3">
              Username бота для парсинга профилей и получения cookies.
            </p>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-xs text-[#8A9099]">Bot Username</Label>
                <div className="flex items-center mt-1">
                  <span className="text-sm text-[#5C6370] mr-1">@</span>
                  <Input
                    value={botUsername}
                    onChange={(e) => setBotUsername(e.target.value.replace('@', ''))}
                    placeholder="ozon_48230_block_bot"
                    className="bg-[#0D0F12] border-[#252A33] font-mono text-xs"
                    data-testid="bot-username-input"
                  />
                </div>
              </div>
              <button
                onClick={saveBotUsername}
                disabled={botSaving}
                className="action-btn action-btn-primary h-9 px-4"
                data-testid="bot-username-save"
              >
                {botSaving ? "..." : "Save"}
              </button>
            </div>
          </div>

          {/* Dolphin Anty API */}
          <div className="bg-[#1A1D24] rounded-lg border border-[#252A33] p-6" data-testid="dolphin-settings">
            <h2 className="text-sm font-semibold text-[#8A9099] mb-4 flex items-center gap-2">
              <Globe size={16} weight="duotone" className="text-[#0288D1]" />
              DOLPHIN ANTY API
            </h2>
            <p className="text-xs text-[#5C6370] mb-3">
              API token for exporting profiles to Dolphin Anty. Get it from Dolphin Anty &rarr; Settings &rarr; API.
            </p>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-xs text-[#8A9099]">API Token</Label>
                <Input
                  type="password"
                  value={dolphinToken}
                  onChange={(e) => setDolphinToken(e.target.value)}
                  placeholder="eyJhbGciOiJSUz..."
                  className="mt-1 bg-[#0D0F12] border-[#252A33] font-mono text-xs"
                  data-testid="dolphin-token-input"
                />
              </div>
              <button
                onClick={saveDolphinToken}
                disabled={dolphinSaving}
                className="action-btn action-btn-primary h-9 px-4"
                data-testid="dolphin-token-save"
              >
                {dolphinSaving ? "..." : "Save"}
              </button>
            </div>
            {dolphinLoaded && dolphinToken && (
              <div className="mt-2 text-xs text-[#4CAF50]">Token set. Select profiles and click "Dolphin" to export.</div>
            )}
          </div>

          {/* App info */}
          <div className="bg-[#1A1D24] rounded-lg border border-[#252A33] p-6">
            <h2 className="text-sm font-semibold text-[#8A9099] mb-4">О ПРИЛОЖЕНИИ</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-[#8A9099]">Версия</span>
                <span className="font-mono">1.1.0</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8A9099]">Тип</span>
                <span>AntiDetect Browser</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8A9099]">Платформа</span>
                <span>Web</span>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="bg-gradient-to-r from-[#2196F3]/10 to-transparent rounded-lg border border-[#2196F3]/30 p-6">
            <div className="flex items-start gap-3">
              <Info size={20} className="text-[#2196F3] mt-0.5" />
              <div>
                <h3 className="font-semibold mb-2">Важная информация</h3>
                <p className="text-sm text-[#8A9099]">
                  Это веб-версия приложения для управления профилями Ozon. 
                  Поддерживает импорт карт через Telegram, управление статусами и мониторинг балансов.
                </p>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="bg-[#1A1D24] rounded-lg border border-[#252A33] p-6">
            <h2 className="text-sm font-semibold text-[#8A9099] mb-4">ВОЗМОЖНОСТИ</h2>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#4CAF50] rounded-full" />
                <span>Управление профилями браузера</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#4CAF50] rounded-full" />
                <span>Импорт данных Ozon карт (card.json)</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#4CAF50] rounded-full" />
                <span>Пользовательские статусы профилей</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#4CAF50] rounded-full" />
                <span>Прокси настройки (HTTP/HTTPS/SOCKS)</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#4CAF50] rounded-full" />
                <span>Импорт профилей из Telegram</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#4CAF50] rounded-full" />
                <span>Экспорт/Импорт профилей (JSON)</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
